/* global Excel, TextDecoder, TextEncoder, atob, btoa */

import type { TraceSheetFilterPersistedV1 } from '../models/trace-sheet-filter-persisted.model';
import { parseTraceSheetFilterPersistedJson } from '../models/trace-sheet-filter-persisted.model';

/** Excel defined name for per-sheet filter JSON (keyed by worksheet id). */
export function workbookNamedItemKeyForSheetId(sheetId: string): string {
    const safe = sheetId.replace(/[^0-9A-Za-z._]/g, '_');
    return `TraceRepSF_${safe}`;
}

function utf8ToBase64Url(json: string): string {
    const utf8 = new TextEncoder().encode(json);
    let binary = '';
    for (let i = 0; i < utf8.length; i++) {
        const b = utf8[i];
        if (b === undefined) {
            continue;
        }
        binary += String.fromCharCode(b);
    }
    const b64 = btoa(binary);
    return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/u, '');
}

function base64UrlToUtf8(b64url: string): string | null {
    try {
        let b64 = b64url.replace(/-/g, '+').replace(/_/g, '/');
        const pad = b64.length % 4;
        if (pad === 2) {
            b64 += '==';
        } else if (pad === 3) {
            b64 += '=';
        } else if (pad === 1) {
            return null;
        }
        const binary = atob(b64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }
        return new TextDecoder('utf-8').decode(bytes);
    } catch {
        return null;
    }
}

/**
 * Excel string formula: `="<payload>"` with Excel-style doubling of embedded `"`.
 * Payload is base64url (no quotes), so no doubling needed.
 */
function excelStringFormulaForPayload(payload: string): string {
    const escaped = payload.replace(/"/g, '""');
    return `="${escaped}"`;
}

function readNamedItemStringValue(raw: unknown): string | null {
    if (typeof raw === 'string') {
        let s = raw;
        if (s.startsWith('=') && s.length >= 2 && s.startsWith('="')) {
            s = s.slice(2);
            if (s.endsWith('"')) {
                s = s.slice(0, -1);
            }
            return s.replace(/""/g, '"');
        }
        return s;
    }
    return null;
}

/**
 * Persists filter snapshot on the workbook as a defined name keyed by worksheet id.
 * Safe to call after a successful tabular write; failures are swallowed by caller if desired.
 */
export async function saveTraceSheetFiltersNamedItemForSheet(
    sheetId: string,
    snapshot: TraceSheetFilterPersistedV1
): Promise<void> {
    const json = JSON.stringify(snapshot);
    const b64 = utf8ToBase64Url(json);
    const name = workbookNamedItemKeyForSheetId(sheetId);
    const formula = excelStringFormulaForPayload(b64);

    return Excel.run(async (context) => {
        const wb = context.workbook;
        const existing = wb.names.getItemOrNullObject(name);
        existing.load('isNullObject');
        await context.sync();
        if (!existing.isNullObject) {
            existing.delete();
            await context.sync();
        }
        wb.names.add(name, formula);
        await context.sync();
    });
}

/**
 * Reads persisted filters for the **active** worksheet, or `null` if none / invalid.
 */
export async function loadTraceSheetFiltersNamedItemForActiveSheet(): Promise<TraceSheetFilterPersistedV1 | null> {
    return Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getActiveWorksheet();
        sheet.load('id');
        await context.sync();
        const name = workbookNamedItemKeyForSheetId(sheet.id);
        const item = context.workbook.names.getItemOrNullObject(name);
        item.load(['isNullObject', 'value']);
        await context.sync();
        if (item.isNullObject) {
            return null;
        }
        const decoded = readNamedItemStringValue(item.value);
        if (!decoded) {
            return null;
        }
        const json = base64UrlToUtf8(decoded.trim());
        if (!json) {
            return null;
        }
        return parseTraceSheetFilterPersistedJson(json);
    });
}
